import { useSyncExternalStore } from 'react';
import { getUserInfo } from './api/userInfo';

const CACHE_KEY = 'admanager_user_context';

const listeners = new Set();

// user_info 请求状态与用户上下文分开维护：即使请求失败，也不能让导航永久停留在 loading。
const serverRequestState = Object.freeze({ loading: false, error: null, loaded: false });
let requestState = serverRequestState;
let refreshSequence = 0;

// useSyncExternalStore 要求 getSnapshot 返回稳定引用，否则每次渲染都会被视为 store 变更触发无限循环。
let cachedRaw = undefined;
let cachedSnapshot = null;

function emit() {
  listeners.forEach((listener) => listener());
}

function setUserContextRequestState(nextState) {
  requestState = Object.freeze({ ...requestState, ...nextState });
  emit();
}

export function subscribeUserContext(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getCachedUserContext() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw !== cachedRaw) {
      cachedRaw = raw;
      cachedSnapshot = raw ? JSON.parse(raw) : null;
    }
  } catch {
    cachedSnapshot = null;
  }
  return cachedSnapshot;
}

function setCachedUserContext(context) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(context));
  emit();
}

export function clearCachedUserContext() {
  localStorage.removeItem(CACHE_KEY);
  emit();
}

export function getUserContextRequestState() {
  return requestState;
}

export function useUserContextRequestState() {
  return useSyncExternalStore(subscribeUserContext, getUserContextRequestState, () => serverRequestState);
}

/**
 * 归一化 admanager /api/user_info 返回结构，仅保留前端需要展示/切换的字段。
 */
export function normalizeUserInfo(info = {}) {
  // relay 返回 { data: {...}, code, message }，userinfo 实际在 data 内；
  // 兼容直连（无 data 包裹）与旧结构。
  const source = info && typeof info === 'object' && info.data && typeof info.data === 'object' ? info.data : info;
  // 可切换角色位于 user_context.available_roles；兼容旧结构回退到顶层 available_roles/active_roles。
  const userContext = source.user_context || {};
  const rawRoles = Array.isArray(userContext.available_roles)
    ? userContext.available_roles
    : Array.isArray(source.available_roles)
      ? source.available_roles
      : Array.isArray(source.active_roles)
        ? source.active_roles
        : [];
  return {
    id: source.id,
    username: source.username || source.name || '',
    nickname: source.nickname || source.username || source.name || '',
    switchable_organizations: Array.isArray(source.switchable_organizations) ? source.switchable_organizations : [],
    current_organization: source.current_organization || null,
    active_role: source.active_role || null,
    available_roles: rawRoles,
    // coop_menus 只用于合作方站点左侧导航展示，缺失时按空菜单处理。
    coop_menus: Array.isArray(userContext.coop_menus) ? userContext.coop_menus : Array.isArray(source.coop_menus) ? source.coop_menus : []
  };
}

/**
 * 调用一次 /api/user_info（经 coop-api 中继），admanager 服务端会同步激活/校验当前组织上下文，
 * 结果缓存到 localStorage 并通知订阅者。
 */
export async function refreshUserContext() {
  const sequence = ++refreshSequence;
  setUserContextRequestState({ loading: true, error: null });

  try {
    const info = await getUserInfo();
    const normalized = normalizeUserInfo(info);
    // 请求可能被后续切换/重试顶替，旧响应不能覆盖新上下文。
    if (sequence !== refreshSequence) return normalized;
    setCachedUserContext(normalized);
    setUserContextRequestState({ loading: false, error: null, loaded: true });
    return normalized;
  } catch (error) {
    // 被后续请求顶替的旧请求不应把新请求的 loading 状态误置为失败。
    if (sequence !== refreshSequence) throw error;
    // 保留已有缓存，调用方可以继续使用旧菜单；无缓存时由导航展示可重试的错误态。
    setUserContextRequestState({ loading: false, error, loaded: requestState.loaded });
    throw error;
  }
}

/**
 * Header 组织/角色切换器使用：读取缓存并订阅变更。
 */
export function useUserContext() {
  return useSyncExternalStore(subscribeUserContext, getCachedUserContext, () => null);
}
